import React from "react";
import ReactModal from "react-modal";
import { motion } from "framer-motion";
import { useCloseOnHistoryBack } from "../hooks/useCloseOnHistoryBack";
import { useOverlayScrollLock } from "../hooks/overlayScrollLock";

/** Common overlay: purple background 80% opacity, no border; prevent scroll on overlay */
const defaultOverlay = {
  /* react-modal omits its default overlay styles when overlayClassName is set; keep full-screen hit target */
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(27, 6, 47, 0.8)",
  border: "none",
  outline: "none",
  overflow: "hidden",
  touchAction: "none",
  overscrollBehavior: "none",
  /* Above the MyZone activity drawer (z: 9995). */
  zIndex: 10010,
};

/** Common content: no border/outline, no rounded corners */
const defaultContent = {
  borderRadius: 0,
  border: "none",
  outline: "none",
  zIndex: 10011,
  /* Overlay uses touch-action:none (inherited). Restore vertical pan so inner scrollers work on phones. */
  touchAction: "pan-y",
};

/**
 * Opacity-only by default. Never use scale/translate: WebGL iframes (Spin /
 * Trivia / Quickfire) often stay blank in Chrome under a transformed ancestor.
 * Pass skipEntranceAnimation for those modals — even opacity:0 on mount can
 * break PIXI/WebGL boot so the Amplify loader spins forever.
 */
const modalContentSpring = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.2, ease: "easeOut" },
};

const modalContentBoxStyle = {
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  flex: 1,
  minHeight: 0,
  maxHeight: "100%",
  overflow: "hidden",
};

/**
 * Drop-in replacement for react-modal that locks background scroll when open
 * (including nested page scroll containers), applies a common purple overlay,
 * and removes borders/outlines from all modals.
 * Wraps content in a spring entrance animation (unless skipEntranceAnimation).
 * Open modals swallow browser / iOS swipe-back so Back closes the overlay first.
 */
export default function Modal(props) {
  const { onAfterOpen, onAfterClose, style, children, skipEntranceAnimation = false, isOpen, onRequestClose, ...rest } = props;
  useCloseOnHistoryBack(Boolean(isOpen), onRequestClose);
  // Effect cleanup runs on unmount / isOpen→false. onAfterClose is skipped when navigating
  // away with the dialog still open, which used to leave the capture-phase wheel lock on.
  useOverlayScrollLock(Boolean(isOpen));
  const mergedStyle = {
    ...style,
    content: { ...defaultContent, ...style?.content },
    overlay: { ...defaultOverlay, ...style?.overlay },
  };
  return (
    <ReactModal
      {...rest}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={mergedStyle}
      onAfterOpen={onAfterOpen}
      onAfterClose={onAfterClose}
    >
      {skipEntranceAnimation ? (
        <div style={modalContentBoxStyle}>{children}</div>
      ) : (
        <motion.div {...modalContentSpring} style={modalContentBoxStyle}>
          {children}
        </motion.div>
      )}
    </ReactModal>
  );
}
